import { UPLOAD_CONFIG } from '../constants/upload';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {number} byteSize
 * @returns {number}
 */
export const computeUploadTimeoutMs = (byteSize) => {
  const sizeMB = byteSize / (1024 * 1024);
  const timeoutMs =
    UPLOAD_CONFIG.BASE_TIMEOUT_MS + Math.ceil(sizeMB * UPLOAD_CONFIG.TIMEOUT_MS_PER_MB);

  return Math.min(Math.max(timeoutMs, UPLOAD_CONFIG.BASE_TIMEOUT_MS), UPLOAD_CONFIG.MAX_TIMEOUT_MS);
};

/**
 * @param {number} status
 * @returns {boolean}
 */
const isRetriableHttpStatus = (status) => {
  if (status === 0) {
    return true;
  }

  return status === 408 || status === 429 || status >= 500;
};

/**
 * @typedef {Object} UploadProgressEvent
 * @property {number} loaded
 * @property {number} total
 * @property {number} percent
 */

/**
 * @typedef {Object} PutBlobOptions
 * @property {(event: UploadProgressEvent) => void} [onProgress]
 * @property {AbortSignal} [signal]
 * @property {boolean} [isPublic]
 * @property {number} [maxRetries]
 */

/**
 * Performs a single PUT to a presigned S3 URL via XMLHttpRequest.
 * Does not attach application auth headers — suitable for direct-to-S3 uploads.
 *
 * @param {string} presignedUrl
 * @param {Blob} body
 * @param {string} contentType
 * @param {PutBlobOptions} [options]
 * @returns {Promise<{ permanentUrl: string, etag: string|null }>}
 */
export const putBlobToPresignedUrl = async (
  presignedUrl,
  body,
  contentType,
  options = {}
) => {
  const {
    onProgress,
    signal,
    isPublic = false,
    maxRetries = UPLOAD_CONFIG.MAX_RETRIES,
  } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (signal?.aborted) {
      throw new Error('Upload cancelled');
    }

    if (attempt > 0) {
      const delayMs = UPLOAD_CONFIG.RETRY_BASE_DELAY_MS * (2 ** (attempt - 1));
      await sleep(delayMs);
    }

    try {
      const etag = await putBlobToPresignedUrlOnce(presignedUrl, body, contentType, {
        onProgress,
        signal,
        isPublic,
      });

      return {
        permanentUrl: presignedUrl.split('?')[0],
        etag,
      };
    } catch (error) {
      lastError = error;

      if (signal?.aborted || !error.retriable) {
        break;
      }
    }
  }

  throw lastError || new Error('Upload failed');
};

/**
 * @param {string} presignedUrl
 * @param {Blob} body
 * @param {string} contentType
 * @param {PutBlobOptions} options
 * @returns {Promise<string|null>}
 */
const putBlobToPresignedUrlOnce = (presignedUrl, body, contentType, options) => {
  const { onProgress, signal, isPublic = false } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.timeout = computeUploadTimeoutMs(body.size);

    const cleanup = () => {
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
    };

    const fail = (message, retriable, status = 0) => {
      cleanup();
      reject(Object.assign(new Error(message), { retriable, status }));
    };

    const onAbort = () => {
      xhr.abort();
      fail('Upload cancelled', false);
    };

    if (signal) {
      if (signal.aborted) {
        fail('Upload cancelled', false);
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable || !onProgress) {
        return;
      }

      onProgress({
        loaded: event.loaded,
        total: event.total,
        percent: Math.round((event.loaded * 100) / event.total),
      });
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        cleanup();
        resolve(xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag'));
        return;
      }

      fail(
        `Upload failed with status ${xhr.status}`,
        isRetriableHttpStatus(xhr.status),
        xhr.status
      );
    });

    xhr.addEventListener('error', () => {
      fail('Upload failed due to a network error', true);
    });

    xhr.addEventListener('timeout', () => {
      fail('Upload timed out. Check your connection and try again.', true);
    });

    xhr.addEventListener('abort', () => {
      fail('Upload cancelled', false);
    });

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', contentType);

    if (isPublic) {
      xhr.setRequestHeader('x-amz-acl', 'public-read');
    }

    xhr.send(body);
  });
};

/**
 * Maps raw transfer progress (0–100) to the UI upload phase (5–95).
 * @param {number} transferPercent
 * @returns {number}
 */
export const mapTransferProgressToUi = (transferPercent) => {
  const clamped = Math.max(0, Math.min(100, transferPercent));
  const transferSpan = UPLOAD_CONFIG.PROGRESS.TRANSFER_MAX - UPLOAD_CONFIG.PROGRESS.PREPARING_MAX;

  return Math.round(
    UPLOAD_CONFIG.PROGRESS.PREPARING_MAX + (clamped * transferSpan) / 100
  );
};

/**
 * Maps bundle file progress into the shared UI scale.
 * @param {number} fileIndex - zero-based index of the current file
 * @param {number} totalFiles
 * @param {number} fileTransferPercent - 0–100 for the current file
 * @returns {number}
 */
export const mapBundleFileProgressToUi = (fileIndex, totalFiles, fileTransferPercent) => {
  if (totalFiles <= 0) {
    return UPLOAD_CONFIG.PROGRESS.PREPARING_MAX;
  }

  const aggregateTransfer =
    ((fileIndex + Math.max(0, Math.min(100, fileTransferPercent)) / 100) / totalFiles) * 100;

  return mapTransferProgressToUi(aggregateTransfer);
};
